import React from 'react';
import { ST_EXCLUDED_ZIPS } from '../../growth-settings/mockData';
import {
  StAddChip,
  StChip,
  StFieldHeading,
  StSectionCard,
  type StSectionSaveFooter,
} from './SettingsPrimitives';

export interface SettingsTargetAreaProps {
  targetRadiusMiles?: number;
  competitorScanRadiusKm?: number;
  excludedPostalCodes?: string[];
  onTargetRadiusMilesChange?: (value: number) => void;
  onCompetitorScanRadiusKmChange?: (value: number) => void;
  onExcludedPostalCodesChange?: (codes: string[]) => void;
  saveFooter?: StSectionSaveFooter;
}

export function SettingsTargetArea({
  targetRadiusMiles = 8,
  competitorScanRadiusKm = 20,
  excludedPostalCodes = ST_EXCLUDED_ZIPS,
  onTargetRadiusMilesChange,
  onCompetitorScanRadiusKmChange,
  onExcludedPostalCodesChange,
  saveFooter,
}: SettingsTargetAreaProps) {
  const handleAddZip = () => {
    const next = window.prompt('Enter a postal code to exclude:');
    if (!next?.trim()) return;
    const code = next.trim().toUpperCase();
    if (excludedPostalCodes.includes(code)) return;
    onExcludedPostalCodesChange?.([...excludedPostalCodes, code]);
  };

  const handleRemoveZip = (code: string) => {
    onExcludedPostalCodesChange?.(excludedPostalCodes.filter((item) => item !== code));
  };

  return (
    <StSectionCard
      title="Target Area"
      description="Define where your ads show and where ELVA looks for competitors."
      {...(saveFooter ? { saveFooter } : {})}
    >
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <label className="block">
          <span className="text-[13.5px] font-medium text-[var(--ink-1)]">Default Ad Radius</span>
          <span className="mt-1 block text-[13px] text-[var(--ink-3)]">
            Default radius for new campaigns. Individual campaigns can override this.
          </span>
          <span className="mt-2 flex h-11 items-center gap-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4">
            <input
              value={String(targetRadiusMiles)}
              onChange={(event) => {
                const n = Number(event.target.value);
                if (Number.isFinite(n)) onTargetRadiusMilesChange?.(n);
              }}
              inputMode="numeric"
              className="h-full min-w-0 flex-1 border-none bg-transparent text-[15px] font-medium text-[var(--ink-1)] outline-none"
            />
            <span className="text-[13px] text-[var(--ink-4)]">miles</span>
          </span>
        </label>
        <label className="block">
          <span className="text-[13.5px] font-medium text-[var(--ink-1)]">
            Competitor Scan Radius
          </span>
          <span className="mt-1 block text-[13px] text-[var(--ink-3)]">
            ELVA scans for competitors within this distance from your clinic.
          </span>
          <span className="mt-2 flex h-11 items-center gap-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4">
            <input
              value={String(competitorScanRadiusKm)}
              onChange={(event) => {
                const n = Number(event.target.value);
                if (Number.isFinite(n)) onCompetitorScanRadiusKmChange?.(n);
              }}
              inputMode="numeric"
              className="h-full min-w-0 flex-1 border-none bg-transparent text-[15px] font-medium text-[var(--ink-1)] outline-none"
            />
            <span className="text-[13px] text-[var(--ink-4)]">km</span>
          </span>
        </label>
      </section>

      <section>
        <StFieldHeading
          title="Excluded Zip Codes (optional)"
          sub="Ads will not show in these areas even if they're within your radius."
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {excludedPostalCodes.map((zip) => (
            <button
              key={zip}
              type="button"
              onClick={() => handleRemoveZip(zip)}
              className="cursor-pointer border-0 bg-transparent p-0"
              title="Remove"
            >
              <StChip>{zip}</StChip>
            </button>
          ))}
          <StAddChip label="+ Add Zip Code" onClick={handleAddZip} />
        </div>
      </section>
    </StSectionCard>
  );
}
